import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  ArrowLeft, 
  Bot, 
  Brain, 
  Play, 
  Pause, 
  Trash2, 
  TrendingUp, 
  Calendar,
  DollarSign,
  Activity,
  Target,
  Settings,
  ChevronLeft,
  ChevronRight,
  Loader2,
  GitBranch,
  BarChart3,
  TrendingDown,
  Percent
} from 'lucide-react'
import { botsApi } from '../lib/api'
import { formatCurrency, formatDate, formatPercent } from '../lib/utils'
import { cn } from '../lib/utils'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts'

interface Execution {
  id: string
  type: string
  status: string
  side: string
  size: number
  price: number
  executedPrice?: number
  pnl?: number
  confidence: number
  reasoning: string
  createdAt: string
  executedAt?: string
  market?: {
    question: string
    category: string
  }
}

interface Analytics {
  period: string
  summary: {
    totalTrades: number
    winningTrades: number
    losingTrades: number
    winRate: number
    totalPnl: number
    avgPnl: number
    avgWin: number
    avgLoss: number
    profitFactor: number
    sharpeRatio: number
    maxDrawdown: number
  }
  dailyPnl: Array<{ date: string; pnl: number }>
  recentExecutions: Execution[]
}

export function BotDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(1)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [analyticsDays, setAnalyticsDays] = useState(30)

  const { data: bot, isLoading: botLoading } = useQuery({
    queryKey: ['bot', id],
    queryFn: () => botsApi.getById(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: executionsData, isLoading: execsLoading } = useQuery({
    queryKey: ['bot-executions', id, currentPage],
    queryFn: () => botsApi.getExecutions(id!, { page: currentPage, limit: 20 }),
    enabled: !!id,
  })

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery<Analytics>({
    queryKey: ['bot-analytics', id, analyticsDays],
    queryFn: () => botsApi.getAnalytics(id!, { days: analyticsDays }).then(r => r.data),
    enabled: !!id,
  })

  const toggleMutation = useMutation({
    mutationFn: () => botsApi.toggle(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bot', id] }),
  })

  const deleteMutation = useMutation({
    mutationFn: () => botsApi.delete(id!),
    onSuccess: () => {
      navigate('/bots')
    },
  })

  if (botLoading || !bot) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  const executions: Execution[] = executionsData?.data?.executions || []
  const pagination = executionsData?.data?.pagination
  const analytics = analyticsData

  // Calculate stats from analytics if available, otherwise from executions
  const totalExecutions = analytics?.summary.totalTrades || bot._count?.executions || 0
  const winningTrades = analytics?.summary.winningTrades || 0
  const totalPnl = analytics?.summary.totalPnl || 0
  const winRate = analytics?.summary.winRate ? analytics.summary.winRate * 100 : 0
  const profitFactor = analytics?.summary.profitFactor || 0
  const sharpeRatio = analytics?.summary.sharpeRatio || 0
  const maxDrawdown = analytics?.summary.maxDrawdown || 0

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800'
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'EXECUTING':
        return 'bg-blue-100 text-blue-800'
      case 'FAILED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'AI_SIGNAL':
        return <Brain className="w-4 h-4" />
      case 'COPY_TRADE':
        return <TrendingUp className="w-4 h-4" />
      default:
        return <Bot className="w-4 h-4" />
    }
  }

  const dailyPnlData = analytics?.dailyPnl || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/bots')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">{bot.name}</h1>
              <span className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                bot.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              )}>
                {bot.isActive ? 'Active' : 'Paused'}
              </span>
              {bot.strategy && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <GitBranch className="w-3 h-3 mr-1" />
                  {bot.strategy.name}
                </span>
              )}
            </div>
            <p className="text-gray-500">{bot.mode.replace('_', ' ')}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <Settings className="w-4 h-4 mr-2" />
            Edit
          </button>
          <button
            onClick={() => toggleMutation.mutate()}
            disabled={toggleMutation.isPending}
            className={cn(
              'flex items-center px-4 py-2 rounded-lg font-medium',
              bot.isActive 
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                : 'bg-green-100 text-green-800 hover:bg-green-200'
            )}
          >
            {toggleMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : bot.isActive ? (
              <Pause className="w-4 h-4 mr-2" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            {bot.isActive ? 'Pause' : 'Activate'}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total P&L</p>
              <p className={cn(
                'text-2xl font-bold',
                totalPnl >= 0 ? 'text-green-600' : 'text-red-600'
              )}>
                {totalPnl >= 0 ? '+' : ''}{formatCurrency(totalPnl)}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold text-gray-900">{winRate.toFixed(1)}%</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Trades</p>
              <p className="text-2xl font-bold text-gray-900">{totalExecutions}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Activity className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Profit Factor</p>
              <p className="text-2xl font-bold text-gray-900">
                {profitFactor === Infinity ? '∞' : profitFactor.toFixed(2)}
              </p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Sharpe Ratio</p>
              <p className="text-2xl font-bold text-gray-900">{sharpeRatio.toFixed(2)}</p>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <Percent className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Max Drawdown</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(maxDrawdown)}</p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Charts */}
      {analyticsLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : analytics && dailyPnlData.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Performance Analytics
            </h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Period:</span>
              {[7, 30, 60, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setAnalyticsDays(days)}
                  className={cn(
                    'px-3 py-1 text-sm rounded-lg transition-colors',
                    analyticsDays === days
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyPnlData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke="#9CA3AF"
                  fontSize={12}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                  stroke="#9CA3AF"
                  fontSize={12}
                />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), 'P&L']}
                  labelFormatter={(label) => formatDate(label as string)}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                  {dailyPnlData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.pnl >= 0 ? '#10B981' : '#EF4444'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
            <div>
              <p className="text-sm text-gray-500">Avg Trade P&L</p>
              <p className={cn(
                'text-lg font-semibold',
                (analytics.summary.avgPnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              )}>
                {formatCurrency(analytics.summary.avgPnl || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg Win</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(analytics.summary.avgWin || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg Loss</p>
              <p className="text-lg font-semibold text-red-600">
                {formatCurrency(analytics.summary.avgLoss || 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Wins / Losses</p>
              <p className="text-lg font-semibold text-gray-900">
                {analytics.summary.winningTrades} / {analytics.summary.losingTrades}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Max Position</p>
            <p className="font-medium">{formatCurrency(bot.maxPositionSize)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Kelly Fraction</p>
            <p className="font-medium">{(bot.kellyFraction * 100).toFixed(0)}%</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Daily Loss Limit</p>
            <p className="font-medium">{formatCurrency(bot.maxDailyLoss)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Max Open Positions</p>
            <p className="font-medium">{bot.maxOpenPositions}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Min Sharpe Ratio</p>
            <p className="font-medium">{bot.minSharpeRatio}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Min Win Rate</p>
            <p className="font-medium">{(bot.minWinRate * 100).toFixed(0)}%</p>
          </div>
        </div>
        {bot.categoryFilter?.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">Category Filter</p>
            <div className="flex flex-wrap gap-2">
              {bot.categoryFilter.map((cat: string) => (
                <span key={cat} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                  {cat}
                </span>
              ))}
            </div>
          </div>
        )}
        {bot.strategy && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-2">Linked Strategy</p>
            <div className="flex items-center p-3 bg-purple-50 rounded-lg">
              <GitBranch className="w-5 h-5 text-purple-600 mr-3" />
              <div>
                <p className="font-medium text-purple-900">{bot.strategy.name}</p>
                <p className="text-sm text-purple-600">
                  {bot.strategy.rules?.length || 0} rules configured
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Execution History */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Execution History</h2>
        </div>

        {execsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : executions.length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No executions yet</p>
            <p className="text-sm text-gray-400 mt-1">Trades will appear here when the bot executes</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Market</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Side</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P&L</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {executions.map((execution: Execution) => (
                    <tr key={execution.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-gray-400 mr-2">{getTypeIcon(execution.type)}</span>
                          <span className="text-sm text-gray-900">{execution.type.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900 max-w-xs truncate">
                          {execution.market?.question || 'Unknown Market'}
                        </p>
                        {execution.market?.category && (
                          <span className="text-xs text-gray-500">{execution.market.category}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                          execution.side === 'YES' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        )}>
                          {execution.side}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(execution.size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {execution.price.toFixed(3)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                          getStatusColor(execution.status)
                        )}>
                          {execution.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {execution.pnl !== undefined && execution.pnl !== null ? (
                          <span className={cn(
                            'text-sm font-medium',
                            execution.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                          )}>
                            {execution.pnl >= 0 ? '+' : ''}{formatCurrency(execution.pnl)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {execution.executedAt 
                          ? formatDate(execution.executedAt)
                          : formatDate(execution.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {pagination.pages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={currentPage === pagination.pages}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Bot</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{bot.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
